/**
 * inspectionUploadStore - Guarda no aparelho (IndexedDB) as fotos/anexos da vistoria externa
 * até serem salvos na vistoria. Permite retomar o envio ao reabrir o link após fechar a página,
 * descarregar o celular ou perder a internet.
 *
 * - Banco `uaizy-vistoria-uploads`, uma entrada por arquivo, filtrada pelo token do link.
 * - Entradas com mais de 7 dias são apagadas automaticamente.
 * - Sem IndexedDB (modo anônimo/sem espaço), todas as funções falham em silêncio e `available`
 *   retorna false: o envio segue só em memória.
 *
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { logger } from '@/lib/logger';

export const UPLOAD_STORE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const DB = 'uaizy-vistoria-uploads';
const STORE = 'uploads';

export interface StoredUpload<A = unknown> {
  id: string;
  linkToken: string;
  answerId: string;
  blob: Blob;
  name: string;
  type: string;
  fromCamera: boolean;
  uploaded: A | null;
  createdAt: number;
}

/** Entradas vencidas (mais de 7 dias). Puro, para teste. */
export const isExpired = (createdAt: number, now = Date.now()) => now - createdAt > UPLOAD_STORE_MAX_AGE_MS;

let dbPromise: Promise<IDBDatabase> | null = null;
function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB indisponível')); return; }
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      const s = req.result.createObjectStore(STORE, { keyPath: 'id' });
      s.createIndex('linkToken', 'linkToken');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then((db) => new Promise<T | undefined>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(req ? (req.result as T) : undefined);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

/** Testa se o aparelho permite guardar (abre o banco). */
export async function uploadStoreAvailable() {
  try { await open(); return true; } catch { return false; }
}

export async function putUpload<A>(entry: StoredUpload<A>) {
  try { await tx('readwrite', (s) => s.put(entry)); return true; }
  catch (e) { logger.warn('Não foi possível guardar o arquivo no aparelho', (e as Error)?.message); return false; }
}

/** Marca o arquivo como enviado (guarda só os metadados para regravar a resposta). */
export async function markUploaded<A>(id: string, uploaded: A) {
  try {
    const cur = await tx<StoredUpload<A>>('readonly', (s) => s.get(id));
    if (cur) await tx('readwrite', (s) => s.put({ ...cur, uploaded }));
  } catch { /* segue só em memória */ }
}

export async function deleteUpload(id: string) {
  try { await tx('readwrite', (s) => s.delete(id)); } catch { /* sem armazenamento */ }
}

/** Lista as entradas do link e apaga as vencidas (de qualquer link). */
export async function listUploads<A>(linkToken: string): Promise<StoredUpload<A>[]> {
  try {
    const all = (await tx<StoredUpload<A>[]>('readonly', (s) => s.getAll())) ?? [];
    const expired = all.filter((e) => isExpired(e.createdAt));
    await Promise.all(expired.map((e) => deleteUpload(e.id)));
    return all.filter((e) => e.linkToken === linkToken && !isExpired(e.createdAt)).sort((a, b) => a.createdAt - b.createdAt);
  } catch { return []; }
}
